/* eslint-disable */
// /src/services/authService.js
// 역할:
//  - SignupPage / LoginPage 에서 사용하는 인증 진입점
//  - Firestore members 컬렉션을 기준으로 로그인/가입 처리
//  - 내부 storage에는 "전화번호(phoneE164)만" 저장 → 자동 로그인 키로 사용
//  - ✅ 정책 변경: 어디서든 로그인 OK (deviceKey/bindKey/DEVICE_MISMATCH/confirmDeviceBind 제거)

import {
    saveSession,
    getSessionRaw,
    clearSession,
    saveUser,
    getUserRaw,
    clearUser,
    clearMembership,
} from "./storage";

import {
    isPhoneRegisteredInMembers,
    isUsernameTaken,
    findMemberByUsernamePassword,
    findMemberByPhone,
    createMember,
} from "./memberDataService";

/**
 * 세션 시작 헬퍼
 * - Firestore에서 읽어온 member 객체를 기준으로 세션/자동로그인 정보 세팅
 * - 요구사항: 내부 storage에는 phoneE164 정도만 저장
 */
function startSessionForMember(member) {
    const now = Date.now();

    // ✅ SSOT: phoneE164는 반드시 있어야 함 (폴백 금지)
    const phoneE164 = member?.phoneE164;
    if (!phoneE164) {
        throw new Error("회원 정보(전화번호)가 누락되었습니다. 관리자에게 문의해 주세요.");
    }

    const token = `${phoneE164}.${now}`;

    // ✅ 세션 정보: auth는 member로 통일 (중요)
    saveSession({
        token,
        phoneE164,
        at: now,
        memberId: member.id || null,
        username: member.username || null,
        auth: "member",
    });

    // ✅ 자동 로그인용 (phoneE164만)
    saveUser({ phoneE164 });

    // ✅ LoginPage에서 dhm.session 만들 때 쓸 정보까지 함께 반환
    return {
        ok: true,
        token,
        phoneE164,
        username: member.username || null,
        name: member.name || "",
        memberType: member.memberType || null,
    };
}

/**
 * 외부에서 중복 체크할 때 사용 (전화번호 기준)
 * - SignupPage에서 "이미 가입된 회원입니다" 체크 용도
 */
export async function isPhoneRegistered(phoneE164) {
    return isPhoneRegisteredInMembers(phoneE164);
}

/**
 * 회원가입
 * - Firestore members 컬렉션에 문서 1개 생성
 * - 생성 후 바로 로그인 상태로 전환
 * - ✅ 정책 변경: bindKey/deviceKey 저장/바인딩 없음
 */
export async function signup(input) {
    const {
        username,
        password,
        name,
        phoneE164,
        carNumber,
        memberType, // "personal" | "corporate" | "general"
    } = input || {};

    if (!username || !password || !name || !phoneE164 || !carNumber) {
        throw new Error("필수 항목이 누락되었습니다.");
    }

    if (!memberType) {
        throw new Error("회원 유형을 선택해 주세요.");
    }

    // 아이디 중복 체크
    if (await isUsernameTaken(username)) {
        throw new Error("이미 사용 중인 아이디입니다.");
    }

    // 전화번호 중복 체크
    if (await isPhoneRegisteredInMembers(phoneE164)) {
        throw new Error("이미 가입된 회원입니다.");
    }

    // Firestore members 컬렉션에 새 멤버 생성
    const member = await createMember({
        username,
        password,
        name,
        phoneE164,
        carNumber,
        memberType,
    });

    // 가입 직후 바로 세션 시작
    return startSessionForMember(member);
}

/**
 * 로그인
 * - username / password 기준으로 Firestore members 조회
 * - ✅ 정책 변경: 어디서든 로그인 OK (deviceKey/bindKey/DEVICE_MISMATCH 제거)
 * - 성공 시 세션/자동로그인 정보 세팅
 */
export async function login(input) {
    const { username, password } = input || {};
    if (!username || !password) {
        throw new Error("아이디와 비밀번호를 입력해주세요.");
    }

    const member = await findMemberByUsernamePassword(username, password);
    if (!member) {
        throw new Error("아이디 또는 비밀번호가 올바르지 않습니다.");
    }

    // 🔴 승인 거부 상태 체크
    if (member.memberStatus === "rejected") {
        throw new Error("승인이 거절된 회원입니다.\n자세한 내용은 관리자에게 문의해 주세요.");
    }

    // ✅ 세션 생성
    return startSessionForMember(member);
}

/**
 * 로그아웃
 * - 세션/유저/멤버십 캐시 초기화
 */
export async function logout() {
    clearSession();
    clearUser();
    clearMembership();
}

/**
 * 세션 객체(raw) 반환
 * - { token, phoneE164, at, memberId?, username? } 형태일 수 있음
 */
export function getSession() {
    return getSessionRaw();
}

/**
 * 현재 저장된 "자동로그인 키" 조회
 * - 기존 코드 호환용: storage에 저장해 둔 userRaw 그대로 반환
 * - 지금은 { phoneE164 } 정도만 들어있다고 보면 됨
 */
export function getUser() {
    return getUserRaw();
}

/**
 * 현재 로그인한 멤버를 Firestore에서 다시 로드
 * - 자동 로그인/앱 시작 시, phoneE164 기준으로 members 컬렉션에서 조회
 */
export async function getCurrentMember() {
    const raw = getUserRaw();
    const phoneE164 = raw?.phoneE164;
    if (!phoneE164) return null;

    const member = await findMemberByPhone(phoneE164);
    return member || null;
}

/**
 * approvals / settlements 에서 사용할 키 헬퍼
 * - phoneE164 / carNumber 페어를 가져오기 위한 함수
 */
export async function getMemberKey() {
    const member = await getCurrentMember();

    console.log("getmember", member);

    if (!member) return null;
    return {
        phoneE164: member.phoneE164,
        carNumber: member.carNumber,
        name: member.name,
        username: member.username,
        memberId: member.id || null,
        memberType: member.memberType || null,
    };
}
